import type {
  MpClient,
  MpEvent,
  MpPositionTick,
  MpRoomState,
} from "./types";
import type { AvatarConfig } from "@shared/types";
import {
  HEARTBEAT_INTERVAL_MS,
  PROTOCOL_VERSION,
} from "./protocol";

/**
 * Production transport — talks to the Cloudflare RaceRoom Durable Object via
 * WebSocket. v2 protocol: versioned hello, heartbeat ping every 2s,
 * sequence-numbered position ticks.
 *
 * Endpoint: wss://<host>/race/ws/<CODE>
 *
 * Auto-reconnect on transient disconnects (5 attempts with backoff).
 */
export class WebSocketTransport implements MpClient {
  private ws: WebSocket | null = null;
  private url = "";
  private myId = "";
  private listeners: Array<(e: MpEvent) => void> = [];
  private reconnects = 0;
  private hello: {
    asHost: boolean;
    me: { id: string; displayName: string; avatarConfig: AvatarConfig };
    lessonId?: string;
    seed?: number;
    visibility?: "open" | "private";
  } | null = null;
  private heartbeatTimer: number | null = null;
  private tickSeq = 0;
  /** Per-peer last seen sequence so we can drop out-of-order ticks. */
  private peerSeq = new Map<string, number>();

  async join(opts: {
    code: string;
    asHost: boolean;
    me: { id: string; displayName: string; avatarConfig: AvatarConfig };
    lessonId?: string;
    seed?: number;
    visibility?: "open" | "private";
  }): Promise<MpRoomState> {
    this.myId = opts.me.id;
    this.hello = { asHost: opts.asHost, me: opts.me, lessonId: opts.lessonId, seed: opts.seed, visibility: opts.visibility };
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.url = `${proto}://${location.host}/race/ws/${opts.code}`;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Race server didn't respond")), 5000);
      const off = this.on("event", (e) => {
        if (e.type === "state" && e.state.players.some((p) => p.id === this.myId)) {
          clearTimeout(timer);
          off();
          resolve(e.state);
        } else if (e.type === "error") {
          clearTimeout(timer);
          off();
          reject(new Error(e.message));
        }
      });
      this.connect();
    });
  }

  private connect() {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.reconnects = 0;
      if (this.hello) {
        ws.send(
          JSON.stringify({
            type: "hello",
            v: PROTOCOL_VERSION,
            asHost: this.hello.asHost,
            me: { ...this.hello.me, slotIdx: 0, ready: false, finished: false },
            lessonId: this.hello.lessonId,
            seed: this.hello.seed,
            visibility: this.hello.visibility,
          }),
        );
      }
      this.startHeartbeat();
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "state") {
          this.emit({ type: "state", state: msg.state });
        } else if (msg.type === "tick") {
          // Per-peer monotonic seq. Drop *slight* backward jumps (out-of-order
          // packet arrival) but accept *large* backward jumps as a reconnect —
          // the peer's outgoing tickSeq resets to 0 on a fresh WS, and we'd
          // otherwise silently discard every post-reconnect tick forever.
          const from: string = msg.from;
          const seq: number = msg.tick?.seq ?? 0;
          const lastSeq = this.peerSeq.get(from) ?? -1;
          if (seq <= lastSeq && (lastSeq - seq) < 50) return;
          this.peerSeq.set(from, seq);
          this.emit({
            type: "tick",
            from,
            tick: {
              s: msg.tick.s,
              laneOffset: msg.tick.l ?? msg.tick.laneOffset ?? 0,
              state: msg.tick.st ?? msg.tick.state ?? "run",
              velocityS: msg.tick.vs ?? msg.tick.velocityS,
              velocityL: msg.tick.vl ?? msg.tick.velocityL,
              // Authoritative jump fields. `y / vy / gr / js` are the
              // short wire keys; old clients may still send the long
              // names so we accept both.
              y: msg.tick.y ?? msg.tick.yOffset,
              vy: msg.tick.vy,
              grounded: msg.tick.gr ?? msg.tick.grounded,
              jumpSeq: msg.tick.js ?? msg.tick.jumpSeq,
            },
          });
        } else if (msg.type === "leave") {
          this.peerSeq.delete(msg.from);
          this.emit({ type: "leave", from: msg.from });
        } else if (msg.type === "pong") {
          // Heartbeat ack — could measure RTT here in future.
        } else if (msg.type === "error") {
          this.emit({ type: "error", message: msg.message ?? "Battle room error" });
        }
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => {
      this.ws = null;
      this.stopHeartbeat();
      if (this.reconnects < 5 && this.hello) {
        this.reconnects++;
        setTimeout(() => this.connect(), Math.min(2000, 200 * 2 ** this.reconnects));
      }
    };
    ws.onerror = () => {
      try { ws.close(); } catch { /* ignore */ }
    };
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      this.send({ type: "ping", at: Date.now() });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer != null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  setReady(ready: boolean): void {
    this.send({ type: "set-ready", ready });
  }

  startRace(): void {
    this.send({ type: "start-race" });
  }

  backToLobby(): void {
    this.send({ type: "back-to-lobby" });
  }

  changeLesson(lessonId: string): void {
    this.send({ type: "change-lesson", lessonId });
  }

  nextLevel(lessonId: string): void {
    this.send({ type: "next-level", lessonId });
  }

  restartSameLevel(): void {
    this.send({ type: "restart-same-level" });
  }

  vote(lessonId: string): void {
    this.send({ type: "vote", lessonId });
  }

  setQuizStyle(quizStyle: string): void {
    this.send({ type: "set-quiz-style", quizStyle });
  }

  setHostPaused(paused: boolean): void {
    this.send({ type: "set-host-paused", paused: !!paused });
  }

  sendTick(tick: MpPositionTick): void {
    this.tickSeq++;
    // Wire format compresses field names to keep tick payload small.
    // `y / vy / gr / js` are the authoritative jump-state fields read
    // by remote ghosts to mirror the peer's actual jump arc + double-
    // jump pulses. Without them the ghost only saw the "jump" state
    // flag and lerped to a fixed height, so the animation played but
    // the avatar didn't actually move up.
    this.send({
      type: "tick",
      tick: {
        seq: this.tickSeq,
        s: tick.s,
        l: tick.laneOffset,
        st: tick.state,
        vs: tick.velocityS ?? 0,
        vl: tick.velocityL ?? 0,
        y: tick.y,
        vy: tick.vy,
        gr: tick.grounded,
        js: tick.jumpSeq,
      },
    });
  }

  reportFinish(opts: {
    timeMs: number;
    score: number;
    correctCount: number;
    totalQuestions: number;
  }): void {
    this.send({ type: "finish", ...opts });
  }

  on(_event: "event", cb: (e: MpEvent) => void): () => void {
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb);
    };
  }

  leave(): void {
    this.send({ type: "leave" });
    this.stopHeartbeat();
    this.hello = null;
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }
    this.listeners = [];
    this.peerSeq.clear();
  }

  private send(msg: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(msg));
    } catch {
      /* socket gone — onclose handler will reconnect */
    }
  }

  private emit(e: MpEvent) {
    for (const l of this.listeners) l(e);
  }
}
